import { joinPromptFragments, normalizePromptFragment, normalizePromptList } from './promptUtils'
import type {
  ConsistencyAssetCollection,
  ConsistencyAssetDraft,
  ConsistencyAssetType,
  PromptMentionOption,
  StoryboardSceneDraft,
} from './types'

const MAX_CORE_PROPS_PER_SCENE = 2

export const CONSISTENCY_ASSET_LABELS: Record<ConsistencyAssetType, string> = {
  character: '角色',
  scene: '场景',
  prop: '道具',
}

export const CONSISTENCY_PROMPT_GUIDES: Record<ConsistencyAssetType, string> = {
  character: '这里只写角色固定外观，例如年龄气质、发型、服饰、身份感和主色调，不要写剧情动作。',
  scene: '这里只写场景固定设定，例如空间结构、时代风格、主色调、光线氛围和关键陈设，不要写剧情事件。',
  prop: '这里只写道具固定造型，例如材质、颜色、工艺、尺寸和年代感，不要写剧情动作。',
}

export const CONSISTENCY_ASSET_MATERIAL_TYPES: Record<ConsistencyAssetType, 'character_image' | 'background_image' | 'prop_image'> = {
  character: 'character_image',
  scene: 'background_image',
  prop: 'prop_image',
}

export const CONSISTENCY_ASSET_TAB_OPTIONS: Array<{ value: ConsistencyAssetType; label: string }> = [
  { value: 'character', label: '角色' },
  { value: 'scene', label: '场景' },
  { value: 'prop', label: '道具' },
]

export type ConsistencyAssetLookups = Record<ConsistencyAssetType, Map<string, ConsistencyAssetDraft>>
export type ConsistencyAssetSelectionOptions = Record<ConsistencyAssetType, Array<{ value: string; label: string }>>

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function normalizeConsistencyText(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

export function normalizeSceneAssetName(value: unknown) {
  const normalized = normalizeConsistencyText(value)
  if (!normalized) {
    return ''
  }

  const matched = normalized.match(/^镜头\s*(\d+)$/)
  if (matched) {
    return `场景 ${matched[1]}`
  }

  return normalized
}

export function collapseConsistencyAssetText(prompt: unknown, notes: unknown) {
  const normalizedPrompt = normalizeConsistencyText(prompt)
  const normalizedNotes = normalizeConsistencyText(notes)

  if (!normalizedPrompt) {
    return normalizedNotes
  }

  if (!normalizedNotes || normalizedNotes === normalizedPrompt) {
    return normalizedPrompt
  }

  return joinPromptFragments([normalizedPrompt, normalizedNotes], '；') || normalizedPrompt
}

export function normalizeConsistencyAssetNameKey(value: unknown) {
  return normalizeConsistencyText(value).toLocaleLowerCase()
}

export function normalizeConsistencyAssetImageKey(imageUrl: unknown, materialId?: number) {
  const normalizedMaterialId = Number(materialId || 0)
  if (normalizedMaterialId > 0) {
    return `material:${normalizedMaterialId}`
  }

  const normalizedUrl = normalizeConsistencyText(imageUrl)
  if (!normalizedUrl) {
    return ''
  }

  try {
    const parsed = new URL(normalizedUrl)
    return `${parsed.origin}${parsed.pathname}`.toLocaleLowerCase()
  } catch {
    return normalizedUrl.split('?')[0].trim().toLocaleLowerCase()
  }
}

function getConsistencyAssetConflictLabel(asset: Pick<ConsistencyAssetDraft, 'type' | 'name' | 'id'>) {
  const assetName = normalizeConsistencyText(asset.name || asset.id) || '未命名资产'
  return `${CONSISTENCY_ASSET_LABELS[asset.type]} ${assetName}`
}

export function findConsistencyAssetConflict(assets: ConsistencyAssetCollection) {
  const activeAssets = [
    ...(assets.character || []),
    ...(assets.scene || []),
    ...(assets.prop || []),
  ].filter((item) => !item.excluded)

  const nameMap = new Map<string, ConsistencyAssetDraft>()
  const imageMap = new Map<string, ConsistencyAssetDraft>()

  for (const asset of activeAssets) {
    const normalizedName = normalizeConsistencyAssetNameKey(asset.name)
    if (normalizedName) {
      const existing = nameMap.get(normalizedName)
      if (existing && existing.id !== asset.id) {
        return `一致性资产名称重复：${getConsistencyAssetConflictLabel(existing)} 与 ${getConsistencyAssetConflictLabel(asset)}。请保持每个资产名称唯一。`
      }
      nameMap.set(normalizedName, asset)
    }

    const normalizedImageKey = normalizeConsistencyAssetImageKey(asset.image_url, asset.material_id)
    if (normalizedImageKey) {
      const existing = imageMap.get(normalizedImageKey)
      if (existing && existing.id !== asset.id) {
        return `一致性资产图片冲突：${getConsistencyAssetConflictLabel(existing)} 与 ${getConsistencyAssetConflictLabel(asset)} 绑定了同一张图片。请为每个资产使用独立参考图。`
      }
      imageMap.set(normalizedImageKey, asset)
    }
  }

  return ''
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function hashConsistencyKey(value: string) {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) >>> 0
  }
  return hash.toString(36)
}

export function buildConsistencyAssetId(type: ConsistencyAssetType, sourceKey: string) {
  return `${type}-${hashConsistencyKey(`${type}:${sourceKey}`)}`
}

export function createManualConsistencyAsset(type: ConsistencyAssetType): ConsistencyAssetDraft {
  const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  return {
    id: `${type}-manual-${nonce}`,
    type,
    source_key: `manual:${nonce}`,
    name: '',
    prompt: '',
    notes: '',
    image_url: '',
    selected_image_source: '',
    linked_scene_numbers: [],
  }
}

export function getConsistencyPromptGuide(type: ConsistencyAssetType) {
  return CONSISTENCY_PROMPT_GUIDES[type]
}

export function getConsistencyPromptPlaceholder(type: ConsistencyAssetType, assetName?: string) {
  const name = type === 'scene' ? normalizeSceneAssetName(assetName) : normalizeConsistencyText(assetName)
  if (type === 'character') {
    return name ? `${name}：补充年龄气质、发型、服饰、身份感和主色调` : '例如：二十多岁冷静克制，长发束起，深色官服，金线纹样，气质清冷'
  }
  if (type === 'scene') {
    return name ? `${name}：补充空间结构、建筑风格、主色调、光线氛围和关键陈设` : '例如：古代宫廷正殿，金色龙纹御座，暖金主色，顶光穿透，庄严肃穆'
  }
  return name ? `${name}：补充材质、颜色、纹样、尺寸和年代感` : '例如：温润白玉，鎏金边饰，云纹雕刻，小巧厚重，带轻微使用痕迹'
}

function normalizeConsistencyHintFragment(value: unknown) {
  let normalized = normalizeConsistencyText(value)
  if (!normalized) {
    return ''
  }

  normalized = normalized
    .replace(/^(镜头\s*\d+|画面中|画面里|画面|镜头里|此时|随后|然后|只见|只闻|特写|远景|近景|推进|拉开)/, '')
    .replace(/[“”"']/g, '')
    .trim()

  if (!normalized) {
    return ''
  }

  const excludedFragments = ['镜头', '画面', '对白', '台词', '剧情', '随后', '然后', '此时', '转身', '走向', '说道', '说着', '看向', '抬手', '起身', '群臣', '二人', '一人', '慢慢', '快速']
  if (excludedFragments.some((fragment) => normalized.includes(fragment))) {
    return ''
  }

  if (normalized.length > 24) {
    normalized = normalized.slice(0, 24)
  }

  return normalized
}

function collectConsistencyHintFragments(values: unknown[], limit = 4) {
  const result: string[] = []
  const seen = new Set<string>()

  values.forEach((value) => {
    const fragments = Array.isArray(value)
      ? value.flatMap((item) => String(item || '').split(/[，。；、\n]/))
      : String(value || '').split(/[，。；、\n]/)

    fragments.forEach((fragment) => {
      const normalized = normalizeConsistencyHintFragment(fragment)
      if (!normalized || seen.has(normalized) || result.length >= limit) {
        return
      }
      seen.add(normalized)
      result.push(normalized)
    })
  })

  return result
}

function splitConsistencySourceFragments(values: unknown[]) {
  const result: string[] = []

  values.forEach((value) => {
    const fragments = Array.isArray(value)
      ? value.flatMap((item) => String(item || '').split(/[，。；、\n]/))
      : String(value || '').split(/[，。；、\n]/)

    fragments.forEach((fragment) => {
      const normalized = normalizeConsistencyText(fragment)
      if (normalized) {
        result.push(normalized)
      }
    })
  })

  return result
}

function extractCharacterSpecificHintFragment(characterName: string, fragment: string) {
  const normalizedName = normalizeConsistencyText(characterName)
  const normalizedFragment = normalizeConsistencyText(fragment)
  if (!normalizedName || !normalizedFragment) {
    return ''
  }

  const compactName = normalizedName.replace(/\s+/g, '').toLowerCase()
  const compactFragment = normalizedFragment.replace(/\s+/g, '').toLowerCase()
  if (!compactFragment.includes(compactName)) {
    return ''
  }

  const namePattern = new RegExp(escapeRegExp(normalizedName).replace(/\s+/g, '\\s*'), 'gi')
  const cleaned = normalizedFragment
    .replace(namePattern, '')
    .replace(/^(是|为|在|正|并|又|将|把|与|和|跟|留着|穿着|穿|身穿|戴着|佩戴|手持|拿着|抱着)\s*/, '')
    .replace(/^(一位|一名|一个)\s*/, '')
    .trim()

  return normalizeConsistencyHintFragment(cleaned)
}

function collectCharacterSpecificHintFragments(characterName: string, scene: StoryboardSceneDraft, limit = 4) {
  const result: string[] = []
  const seen = new Set<string>()
  const fragments = splitConsistencySourceFragments([
    scene.description,
    scene.action,
    scene.dialogue_summary,
    Array.isArray(scene.visual_cues) ? scene.visual_cues : [],
  ])

  fragments.forEach((fragment) => {
    if (result.length >= limit) {
      return
    }

    const normalized = extractCharacterSpecificHintFragment(characterName, fragment)
    if (!normalized || seen.has(normalized)) {
      return
    }

    seen.add(normalized)
    result.push(normalized)
  })

  return result
}

export function buildConsistencyPromptSuggestion(type: ConsistencyAssetType, assetName: string, hints: string[] = []) {
  const name = normalizeConsistencyText(assetName)
  const hintText = hints.filter(Boolean).slice(0, 4).join('，')

  if (type === 'character') {
    return joinPromptFragments([name, hintText || (name ? `重点补充${name}的年龄气质、发型、服饰、身份感和主色调` : '重点补充年龄气质、发型、服饰、身份感和主色调')], '：')
  }
  if (type === 'scene') {
    return joinPromptFragments([name, hintText || '重点补充空间结构、建筑风格、主色调、光线氛围和关键陈设'], '：')
  }
  return joinPromptFragments([name, hintText || '重点补充材质、颜色、纹样、尺寸和年代感'], '：')
}

function shouldNormalizeConsistencyPrompt(value: string) {
  const normalized = normalizeConsistencyText(value)
  if (!normalized) {
    return true
  }
  const punctuationCount = (normalized.match(/[，。；]/g) || []).length
  return normalized.length > 120 || punctuationCount > 8 || /(镜头|画面|对白|剧情|随后|然后|此时|走向|看向|抬手|转身)/.test(normalized)
}

export function resolveConsistencyPrompt(type: ConsistencyAssetType, value: string, fallbackPrompt: string) {
  const normalized = normalizeConsistencyText(value)
  if (!normalized || shouldNormalizeConsistencyPrompt(normalized)) {
    return fallbackPrompt
  }
  return normalized
}

export function buildConsistencyAssetLoadingKey(type: ConsistencyAssetType, assetId: string) {
  return `${type}:${assetId}`
}

export function buildConsistencyAssetLookups(assets: ConsistencyAssetCollection): ConsistencyAssetLookups {
  const createLookup = (items: ConsistencyAssetDraft[]) => {
    const map = new Map<string, ConsistencyAssetDraft>()
    items.forEach((item) => {
      if (item.excluded) {
        return
      }
      const sourceKey = normalizeConsistencyText(item.source_key || item.name || item.id)
      const nameKey = normalizeConsistencyText(item.name || item.source_key || item.id)
      if (sourceKey) {
        map.set(sourceKey, item)
      }
      if (nameKey) {
        map.set(nameKey, item)
      }
    })
    return map
  }

  return {
    character: createLookup(assets.character),
    scene: createLookup(assets.scene),
    prop: createLookup(assets.prop),
  }
}

export function buildConsistencyAssetSelectionOptions(assets: ConsistencyAssetCollection): ConsistencyAssetSelectionOptions {
  return {
    character: assets.character
      .filter((item) => !item.excluded && String(item.name || item.id).trim())
      .map((item) => ({ value: item.id, label: item.name || item.id })),
    scene: assets.scene
      .filter((item) => !item.excluded && String(item.name || item.id).trim())
      .map((item) => ({ value: item.id, label: item.name || item.id })),
    prop: assets.prop
      .filter((item) => !item.excluded && String(item.name || item.id).trim())
      .map((item) => ({ value: item.id, label: item.name || item.id })),
  }
}

export function buildStoryboardPromptMentionItems(assets: ConsistencyAssetCollection): PromptMentionOption[] {
  const buildItems = (items: ConsistencyAssetDraft[], typeLabel: string) => items
    .filter((item) => !item.excluded)
    .map((item) => {
      const name = String(item.name || item.id || '').trim()
      if (!name) {
        return null
      }

      return {
        key: `${item.type}:${item.id}`,
        name,
        typeLabel,
        description: joinPromptFragments([
          item.prompt,
          item.notes,
          item.linked_scene_numbers?.length ? `关联场景 ${item.linked_scene_numbers.join('、')}` : '',
          item.image_url ? '已配置官方参考图' : '',
        ], '；') || `${typeLabel}官方资产`,
      }
    })
    .filter(Boolean) as PromptMentionOption[]

  return [
    ...buildItems(assets.character, '角色'),
    ...buildItems(assets.scene, '场景'),
    ...buildItems(assets.prop, '道具'),
  ]
}

function isCorePropCandidate(value: string) {
  const normalized = normalizeConsistencyText(value)
  if (!normalized) {
    return false
  }

  if (normalized.length > 18) {
    return false
  }

  if (/[，。；：、“”"'（）()《》【】\-]/.test(normalized)) {
    return false
  }

  const excludedFragments = ['镜头', '画面', '动作', '表情', '氛围', '灯光', '构图', '背景', '情绪', '节奏', '镜头前', '快速', '慢慢']
  return !excludedFragments.some((fragment) => normalized.includes(fragment))
}

export function collectStoryboardPropCandidates(scene: Record<string, any>) {
  const explicit = normalizePromptList([
    ...(Array.isArray(scene.props) ? scene.props : []),
    ...(Array.isArray(scene.required_props) ? scene.required_props : []),
  ]).filter(isCorePropCandidate)

  const fallback = explicit.length
    ? []
    : normalizePromptList(Array.isArray(scene.visual_cues) ? scene.visual_cues : [])
      .filter(isCorePropCandidate)
      .slice(0, 1)

  return Array.from(new Set([...explicit, ...fallback])).slice(0, MAX_CORE_PROPS_PER_SCENE)
}

export function getSceneBoundConsistencyAssetIds(scene: Record<string, any>, type: ConsistencyAssetType) {
  if (type === 'character') {
    return Array.isArray(scene.official_character_asset_ids)
      ? scene.official_character_asset_ids.map((item: unknown) => String(item || '').trim()).filter(Boolean)
      : []
  }
  if (type === 'scene') {
    return String(scene.official_scene_asset_id || '').trim() ? [String(scene.official_scene_asset_id).trim()] : []
  }
  return Array.isArray(scene.official_prop_asset_ids)
    ? scene.official_prop_asset_ids.map((item: unknown) => String(item || '').trim()).filter(Boolean)
    : []
}

export function getSceneBoundConsistencyAssets(scene: Record<string, any>, type: ConsistencyAssetType, assets: ConsistencyAssetCollection) {
  const ids = getSceneBoundConsistencyAssetIds(scene, type)
  if (!ids.length) {
    return []
  }
  const assetMap = new Map(assets[type]
    .filter((item) => !item.excluded)
    .map((item) => [item.id, item] as const))
  return ids.map((id) => assetMap.get(id)).filter(Boolean) as ConsistencyAssetDraft[]
}

export function findConsistencyAsset(lookups: ConsistencyAssetLookups, type: ConsistencyAssetType, value: unknown) {
  const key = normalizeConsistencyText(value)
  if (!key) {
    return null
  }
  return lookups[type].get(key) || null
}

export function findMentionedConsistencyAssets(text: string, assets: ConsistencyAssetCollection) {
  const matches = Array.from(String(text || '').matchAll(/(?:^|[^\w@])@([^\s@，。；,.;:：!?！？、]+)/g))
  if (!matches.length) {
    return {
      character: [] as ConsistencyAssetDraft[],
      scene: [] as ConsistencyAssetDraft[],
      prop: [] as ConsistencyAssetDraft[],
    }
  }

  const mentionedNames = new Set(matches.map((match) => String(match[1] || '').trim()).filter(Boolean))
  const pickByType = (type: ConsistencyAssetType) => assets[type].filter((item) => {
    if (item.excluded) {
      return false
    }
    const name = String(item.name || item.id || '').trim()
    return Boolean(name && mentionedNames.has(name))
  })

  return {
    character: pickByType('character'),
    scene: pickByType('scene'),
    prop: pickByType('prop'),
  }
}

export function extractPromptMentionNames(text: string) {
  return Array.from(
    new Set(
      Array.from(String(text || '').matchAll(/(?:^|[^\w@])@([^\s@，。；,.;:：!?！？、]+)/g))
        .map((match) => String(match[1] || '').trim())
        .filter(Boolean)
    )
  )
}

export function replacePromptMentions(text: string, assets: ConsistencyAssetDraft[]) {
  return assets.reduce((result, asset) => {
    const name = String(asset.name || '').trim()
    if (!name) {
      return result
    }
    return result.split(`@${name}`).join(name)
  }, String(text || ''))
}

export function buildMentionReferenceLines(assets: ConsistencyAssetDraft[]) {
  return assets.map((asset) => {
    const label = CONSISTENCY_ASSET_LABELS[asset.type] || asset.type
    const detail = joinPromptFragments([
      asset.prompt,
      asset.notes,
      asset.image_url ? '生成时必须严格参考官方图' : '',
    ], '；')
    return `${label} ${asset.name || '未命名'}：${detail || '保持该官方资产设定不变'}`
  })
}

export function collectExplicitConsistencyLinks(scenes: StoryboardSceneDraft[], type: ConsistencyAssetType) {
  const links = new Map<string, Set<number>>()
  scenes.forEach((scene) => {
    const sceneNumber = Number(scene.scene_number || 0)
    if (!sceneNumber) {
      return
    }
    getSceneBoundConsistencyAssetIds(scene, type).forEach((assetId) => {
      const current = links.get(assetId) || new Set<number>()
      current.add(sceneNumber)
      links.set(assetId, current)
    })
  })
  return links
}

export function buildAutoConsistencyAssets(storyboardDraftScenes: StoryboardSceneDraft[]): ConsistencyAssetCollection {
  const characterMap = new Map<string, { name: string; scenes: Set<number>; fragments: Set<string> }>()
  const sceneMap = new Map<string, { name: string; scenes: Set<number>; fragments: Set<string> }>()
  const propMap = new Map<string, { name: string; scenes: Set<number>; fragments: Set<string> }>()

  storyboardDraftScenes.forEach((scene) => {
    const sceneNumber = Number(scene.scene_number || 0)
    const sceneTitle = normalizeSceneAssetName(scene.location || scene.description)
    const scenePromptSeed = collectConsistencyHintFragments([
      scene.location,
      scene.time_of_day,
      scene.mood,
      Array.isArray(scene.visual_cues) ? scene.visual_cues : [],
      scene.description,
    ], 5)

    if (sceneTitle) {
      const sourceKey = normalizeConsistencyText(sceneTitle)
      const current = sceneMap.get(sourceKey) || { name: sceneTitle, scenes: new Set<number>(), fragments: new Set<string>() }
      current.scenes.add(sceneNumber)
      scenePromptSeed.forEach((item) => {
        const normalized = normalizeConsistencyText(item)
        if (normalized) {
          current.fragments.add(normalized)
        }
      })
      sceneMap.set(sourceKey, current)
    }

    normalizePromptList(scene.characters).forEach((name) => {
      const sourceKey = normalizeConsistencyText(name)
      const current = characterMap.get(sourceKey) || { name, scenes: new Set<number>(), fragments: new Set<string>() }
      current.scenes.add(sceneNumber)
      collectCharacterSpecificHintFragments(name, scene, 4).forEach((item) => {
        const normalized = normalizeConsistencyText(item)
        if (normalized) {
          current.fragments.add(normalized)
        }
      })
      characterMap.set(sourceKey, current)
    })

    collectStoryboardPropCandidates(scene).forEach((name) => {
      const sourceKey = normalizeConsistencyText(name)
      const current = propMap.get(sourceKey) || { name, scenes: new Set<number>(), fragments: new Set<string>() }
      current.scenes.add(sceneNumber)
      collectConsistencyHintFragments([
        scene.location,
        scene.time_of_day,
        Array.isArray(scene.visual_cues) ? scene.visual_cues : [],
        scene.description,
      ], 4).forEach((item) => {
        const normalized = normalizeConsistencyText(item)
        if (normalized) {
          current.fragments.add(normalized)
        }
      })
      propMap.set(sourceKey, current)
    })
  })

  const toDraftList = (type: ConsistencyAssetType, source: Map<string, { name: string; scenes: Set<number>; fragments: Set<string> }>) => {
    const list = Array.from(source.entries())
      .map(([sourceKey, item]) => {
        const promptHints = Array.from(item.fragments).slice(0, 4)
        return {
          id: buildConsistencyAssetId(type, sourceKey),
          type,
          source_key: sourceKey,
          name: item.name,
          prompt: buildConsistencyPromptSuggestion(type, item.name, promptHints),
          notes: '',
          image_url: '',
          selected_image_source: '',
          linked_scene_numbers: Array.from(item.scenes).filter(Boolean).sort((left, right) => left - right),
        }
      })
      .sort((left, right) => {
        if (type === 'prop' && left.linked_scene_numbers.length !== right.linked_scene_numbers.length) {
          return right.linked_scene_numbers.length - left.linked_scene_numbers.length
        }
        return left.name.localeCompare(right.name, 'zh-CN')
      })

    return list
  }

  return {
    character: toDraftList('character', characterMap),
    scene: toDraftList('scene', sceneMap),
    prop: toDraftList('prop', propMap),
  }
}

export function mergeConsistencyAssetLists(type: ConsistencyAssetType, candidates: ConsistencyAssetDraft[], existing: ConsistencyAssetDraft[]) {
  const existingBySource = new Map<string, ConsistencyAssetDraft>()
  existing.forEach((item) => {
    const sourceKey = normalizeConsistencyText(item.source_key || item.name || item.id)
    if (sourceKey) {
      existingBySource.set(sourceKey, item)
    }
  })

  const usedIds = new Set<string>()
  const merged = candidates.map((candidate) => {
    const sourceKey = normalizeConsistencyText(candidate.source_key || candidate.name || candidate.id)
    const previous = existingBySource.get(sourceKey)
    if (previous?.id) {
      usedIds.add(previous.id)
    }
    const linkedSceneNumbers = Array.from(new Set([
      ...candidate.linked_scene_numbers,
      ...(previous?.linked_scene_numbers || []),
    ])).filter(Boolean).sort((left, right) => left - right)
    const fallbackPrompt = collapseConsistencyAssetText(candidate.prompt, candidate.notes)
      || buildConsistencyPromptSuggestion(type, previous?.name || candidate.name, [])
    const mergedPrompt = collapseConsistencyAssetText(previous?.prompt || candidate.prompt || '', previous?.notes || candidate.notes || '')

    return {
      ...candidate,
      ...(previous || {}),
      id: previous?.id || candidate.id,
      type,
      source_key: candidate.source_key,
      name: type === 'scene' ? normalizeSceneAssetName(previous?.name || candidate.name) : (previous?.name || candidate.name),
      prompt: resolveConsistencyPrompt(type, mergedPrompt, fallbackPrompt),
      notes: '',
      image_url: previous?.image_url || candidate.image_url || '',
      material_id: previous?.material_id || candidate.material_id,
      selected_image_source: previous?.selected_image_source || candidate.selected_image_source || '',
      generated_prompt: previous?.generated_prompt || candidate.generated_prompt || '',
      linked_scene_numbers: linkedSceneNumbers,
      excluded: previous?.excluded || false,
    }
  })

  existing.forEach((item) => {
    const shouldRetainOrphan = String(item.source_key || '').startsWith('manual:')
      || Boolean(normalizeConsistencyText(item.prompt) || normalizeConsistencyText(item.notes) || normalizeConsistencyText(item.image_url) || normalizeConsistencyText(item.selected_image_source) || item.material_id || item.excluded)
    if (!usedIds.has(item.id) && shouldRetainOrphan) {
      merged.push({
        ...item,
        type,
        image_url: item.image_url || '',
        material_id: item.material_id,
        selected_image_source: item.selected_image_source || '',
        generated_prompt: item.generated_prompt || '',
        excluded: item.excluded || false,
      })
    }
  })

  return merged.sort((left, right) => {
    const leftScene = left.linked_scene_numbers[0] || Number.MAX_SAFE_INTEGER
    const rightScene = right.linked_scene_numbers[0] || Number.MAX_SAFE_INTEGER
    if (leftScene !== rightScene) {
      return leftScene - rightScene
    }
    return left.name.localeCompare(right.name, 'zh-CN')
  })
}

export function mergeConsistencyAssetCollections(primary: ConsistencyAssetCollection, secondary: ConsistencyAssetCollection): ConsistencyAssetCollection {
  return {
    character: mergeConsistencyAssetLists('character', primary.character || [], secondary.character || []),
    scene: mergeConsistencyAssetLists('scene', primary.scene || [], secondary.scene || []),
    prop: mergeConsistencyAssetLists('prop', primary.prop || [], secondary.prop || []),
  }
}

function applyExplicitConsistencyLinks(items: ConsistencyAssetDraft[], explicitLinks: Map<string, Set<number>>) {
  return items.map((item) => ({
    ...item,
    linked_scene_numbers: Array.from(new Set([
      ...item.linked_scene_numbers,
      ...(explicitLinks.get(item.id) ? Array.from(explicitLinks.get(item.id) as Set<number>) : []),
    ])).sort((left, right) => left - right),
  }))
}

export function syncConsistencyAssetsFromStoryboard(options: {
  storyboardDraftScenes: StoryboardSceneDraft[]
  persistedConsistencyAssets: ConsistencyAssetCollection
  currentConsistencyAssets: ConsistencyAssetCollection
  currentTab: ConsistencyAssetType
}) {
  const autoAssets = buildAutoConsistencyAssets(options.storyboardDraftScenes)
  const hasCurrentDraft = Object.values(options.currentConsistencyAssets).some((items) => items.length)
  const base = hasCurrentDraft
    ? mergeConsistencyAssetCollections(options.persistedConsistencyAssets, options.currentConsistencyAssets)
    : options.persistedConsistencyAssets
  const explicitCharacterLinks = collectExplicitConsistencyLinks(options.storyboardDraftScenes, 'character')
  const explicitSceneLinks = collectExplicitConsistencyLinks(options.storyboardDraftScenes, 'scene')
  const explicitPropLinks = collectExplicitConsistencyLinks(options.storyboardDraftScenes, 'prop')

  const assets = {
    character: applyExplicitConsistencyLinks(mergeConsistencyAssetLists('character', autoAssets.character, base.character || []), explicitCharacterLinks),
    scene: applyExplicitConsistencyLinks(mergeConsistencyAssetLists('scene', autoAssets.scene, base.scene || []), explicitSceneLinks),
    prop: applyExplicitConsistencyLinks(mergeConsistencyAssetLists('prop', autoAssets.prop, base.prop || []), explicitPropLinks),
  }

  const nextTab = assets[options.currentTab]?.some((item) => !item.excluded)
    ? options.currentTab
    : CONSISTENCY_ASSET_TAB_OPTIONS.find((item) => assets[item.value].some((asset) => !asset.excluded))?.value || 'character'

  return {
    assets,
    nextTab,
  }
}

export function buildConsistencyAssetsPayload(assets: ConsistencyAssetCollection) {
  const normalizeItems = (items: ConsistencyAssetDraft[]) => cloneValue(items).map((item) => ({
    ...item,
    prompt: collapseConsistencyAssetText(item.prompt, item.notes),
    notes: '',
  }))

  return {
    characters: normalizeItems(assets.character),
    scenes: normalizeItems(assets.scene),
    props: normalizeItems(assets.prop),
  }
}

export function buildConsistencyAssetImagePrompt(asset: ConsistencyAssetDraft) {
  const assetPrompt = normalizeConsistencyText(asset.prompt || asset.notes || asset.name)
  const noTextConstraint = '纯视觉参考图，不要任何文字、标题、标签、编号、对白框、信息面板、海报排版、logo、水印或边框'
  if (asset.type === 'character') {
    return joinPromptFragments([
      asset.name ? `${asset.name}单人角色定版形象` : '单人角色定版形象',
      assetPrompt,
      '只保留单个角色主体，突出五官、发型、服装、配饰、材质和主色调的一致性',
      noTextConstraint,
    ], '。') || `${asset.name || '角色'}单人角色定版形象。${noTextConstraint}`
  }
  if (asset.type === 'scene') {
    return joinPromptFragments([
      asset.name ? `${asset.name}场景定版画面` : '场景定版画面',
      assetPrompt,
      '只表现空间结构、主色调、光线氛围和关键陈设，不要人物设定板或说明卡',
      noTextConstraint,
    ], '。') || `${asset.name || '场景'}场景定版画面。${noTextConstraint}`
  }
  return joinPromptFragments([
    asset.name ? `${asset.name}道具定版静物图` : '道具定版静物图',
    assetPrompt,
    '主体完整清晰，突出材质、颜色、纹样、工艺和比例，不要包装说明页或产品海报',
    noTextConstraint,
  ], '。') || `${asset.name || '道具'}道具定版静物图。${noTextConstraint}`
}

export function buildKnowledgeSearchQueryForConsistencyAsset(asset: ConsistencyAssetDraft) {
  return [asset.name, asset.prompt, asset.notes]
    .filter((item) => String(item || '').trim())
    .join(' ')
    .slice(0, 240)
}